'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Briefcase, FileText, Bell, Settings, LogOut, ChevronLeft, ChevronRight, User, Truck, GraduationCap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';



const navItems = [
  {
    key: 'nav-dashboard',
    label: 'Dashboard',
    href: '/user-dashboard',
    icon: LayoutDashboard,
    badge: null,
  },
  {
    key: 'nav-jobs',
    label: 'Job Listings',
    href: '/job-listings-screen',
    icon: Briefcase,
    badge: '47',
  },
  {
    key: 'nav-applications',
    label: 'My Applications',
    href: '/user-dashboard',
    icon: FileText,
    badge: null,
  },
  {
    key: 'nav-notifications',
    label: 'Notifications',
    href: '/user-dashboard',
    icon: Bell,
    badge: '3',
  },
  {
    key: 'nav-profile',
    label: 'My Profile',
    href: '/user-dashboard',
    icon: User,
    badge: null,
  },
  {
    key: 'nav-drivers',
    label: 'Drivers',
    href: '/drivers',
    icon: Truck,
    badge: null,
  },
  {
    key: 'nav-graduates',
    label: 'Graduates',
    href: '/graduates',
    icon: GraduationCap,
    badge: null,
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className="relative flex flex-col bg-card border-r border-border card-shadow transition-all duration-300 ease-in-out shrink-0"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border overflow-hidden">
        <div className="shrink-0">
          <AppLogo
            src="/assets/images/3753d014-4205-457c-8326-992f24d78af6-1784109448561.png"
            size={36}
          />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <span className="font-extrabold text-primary text-base leading-tight block whitespace-nowrap">
              JOB FINDER
            </span>
            <span className="text-muted-foreground text-[10px] font-medium whitespace-nowrap leading-tight">
              FIND YOUR NEXT OPPORTUNITY
            </span>
          </div>
        )}
      </div>
      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-hidden">
        {!collapsed && (
          <p className="text-[10px] font-700 uppercase tracking-widest text-muted-foreground px-3 pb-2">
            Navigation
          </p>
        )}
        {navItems?.map((item) => {
          const Icon = item?.icon;
          const isActive =
            item?.href === '/user-dashboard'
              ? pathname === '/user-dashboard'
              : pathname?.startsWith(item?.href);
          return (
            <Link
              key={item?.key}
              href={item?.href}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              title={collapsed ? item?.label : undefined}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && (
                <span className="flex-1 whitespace-nowrap">{item?.label}</span>
              )}
              {!collapsed && item?.badge && (
                <span className="bg-accent text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {item?.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      {/* Bottom */}
      <div className="px-2 py-4 border-t border-border space-y-1 overflow-hidden">
        <Link
          href="/user-dashboard"
          className="sidebar-nav-item"
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={18} className="shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
        <Link
          href="/"
          className="sidebar-nav-item text-red-500 hover:bg-red-50 hover:text-red-600"
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </Link>
      </div>

      {/* Rocket logo — small, fixed at very bottom */}
      {!collapsed && (
        <div className="px-3 py-2 border-t border-border flex items-center justify-center">
          <span className="text-[9px] text-muted-foreground/50 font-medium tracking-wide">
            Built with Rocket
          </span>
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center shadow-sm hover:bg-secondary transition-colors z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} className="text-muted-foreground" />
        ) : (
          <ChevronLeft size={12} className="text-muted-foreground" />
        )}
      </button>
    </aside>
  );
}